import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, Menu, ChevronDown, ChevronRight, LogOut, Settings as SettingsIcon, 
  User, FileText, Briefcase, Tag, PanelLeftClose, PanelLeftOpen, ArrowLeft, 
  BookOpen, FolderOpen, Mail, MessageSquare, Map, Upload, Star, Award, Trophy, 
  Image, ShoppingBag, Ticket, ClipboardList, Newspaper, Link2, BarChart3, 
  LayoutDashboard, Presentation, Share2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Track expanded submenus
  const [expandedMenus, setExpandedMenus] = useState({});

  // Initialize expanded state based on current path
  useEffect(() => {
    const newExpanded = {};
    MENU_ITEMS.forEach(item => {
      if (item.submenu) {
        // Check if any submenu item is active
        const isActive = item.submenu.some(sub => 
          location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')
        );
        if (isActive) {
          newExpanded[item.id] = true;
        }
      }
    });
    setExpandedMenus(prev => ({ ...prev, ...newExpanded }));
  }, [location.pathname]);

  const toggleMenu = (id) => {
    if (isSidebarCollapsed) {
      setIsSidebarCollapsed(false);
      setExpandedMenus(prev => ({ ...prev, [id]: true }));
    } else {
      setExpandedMenus(prev => ({ ...prev, [id]: !prev[id] }));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const sidebarWidth = isSidebarCollapsed ? 'w-[70px]' : 'w-64';

  // Update body data attribute for toolbar positioning
  useEffect(() => {
    document.body.setAttribute('data-sidebar-collapsed', isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full bg-white border-r border-gray-200 flex flex-col transition-all duration-300 z-[10000] ${sidebarWidth}`}
      >
        {/* Sidebar Header - Fixed at top with higher z-index */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100 flex-shrink-0 bg-white relative z-10">
          {!isSidebarCollapsed && (
            <span className="text-lg font-bold text-[#4C1D95] truncate">Admin Panel</span>
          )}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className={`p-2 rounded-lg text-gray-400 hover:text-[#4C1D95] hover:bg-gray-50 transition-colors ${isSidebarCollapsed ? 'mx-auto' : ''}`}
            title={isSidebarCollapsed ? 'Expand' : 'Collapse'}
          >
            {isSidebarCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 custom-scrollbar">
          {/* Dashboard / Analytics (Main) */}
          <NavItem 
            to="/admin" 
            icon={BarChart3} 
            label="Analytics" 
            isCollapsed={isSidebarCollapsed}
            isActive={location.pathname === '/admin' || location.pathname === '/admin/' || location.pathname.startsWith('/admin/analytics')}
          />

          {MENU_ITEMS.map((item) => (
            <div key={item.id} className="space-y-1">
              {item.submenu ? (
                <>
                  <button
                    onClick={() => toggleMenu(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group
                      ${expandedMenus[item.id] ? 'bg-gray-50 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
                      ${isSidebarCollapsed ? 'justify-center' : 'justify-between'}
                    `}
                    title={isSidebarCollapsed ? item.label : ''}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon size={20} className={`flex-shrink-0 ${expandedMenus[item.id] ? 'text-[#4C1D95]' : 'text-gray-400 group-hover:text-[#4C1D95]'}`} />
                      {!isSidebarCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                    </div>
                    {!isSidebarCollapsed && (
                      <ChevronRight 
                        size={16} 
                        className={`text-gray-400 transition-transform duration-200 ${expandedMenus[item.id] ? 'rotate-90' : ''}`} 
                      />
                    )}
                  </button>
                  
                  {/* Submenu */}
                  <div className={`overflow-hidden transition-all duration-300 ${expandedMenus[item.id] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className={`${isSidebarCollapsed ? 'bg-gray-50 rounded-lg py-1 mt-1' : 'ml-4 pl-3 border-l border-gray-100 space-y-1 mt-1'}`}>
                      {item.submenu.map(sub => (
                        <NavItem
                          key={sub.path}
                          to={sub.path}
                          icon={sub.icon}
                          label={sub.label}
                          isCollapsed={isSidebarCollapsed}
                          isActive={location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')}
                          isSub
                        />
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <NavItem 
                  to={item.path} 
                  icon={item.icon} 
                  label={item.label} 
                  isCollapsed={isSidebarCollapsed}
                  isActive={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
                />
              )}
            </div>
          ))}
        </nav>

        {/* Footer Actions */}
        <div className="p-3 border-t border-gray-100 space-y-1 bg-white flex-shrink-0">
          <NavItem 
            to="/" 
            icon={ArrowLeft} 
            label="Back to Site" 
            isCollapsed={isSidebarCollapsed} 
          />
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-gray-600 hover:bg-red-50 hover:text-red-600 group ${isSidebarCollapsed ? 'justify-center' : ''}`}
            title="Logout"
          >
            <LogOut size={20} className="flex-shrink-0 group-hover:text-red-600 text-gray-400" />
            {!isSidebarCollapsed && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'ml-[70px]' : 'ml-64'} p-8`}>
        <Outlet />
      </main>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 0px;
          background: transparent;
        }
        .custom-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}

// Reusable Nav Item Component
const NavItem = ({ to, icon: Icon, label, isCollapsed, isActive, isSub = false }) => {
  // If sub-item and collapsed, show generic dot or mini icon?
  // User asked for user friendly. Showing tooltip + icon is best.
  
  if (isCollapsed && isSub) {
     // For collapsed sub-items, we render them as small icons
     return (
       <Link
        to={to}
        className={`flex items-center justify-center p-2 rounded-lg transition-all my-0.5
          ${isActive ? 'bg-[#4C1D95]/10 text-[#4C1D95]' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}
        `}
        title={label}
      >
        <Icon size={16} className="flex-shrink-0" />
      </Link>
     );
  }

  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group
        ${isActive 
          ? 'bg-[#4C1D95] text-white shadow-sm' 
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }
        ${isCollapsed ? 'justify-center' : ''}
      `}
      title={isCollapsed ? label : ''}
    >
      <Icon 
        size={isSub ? 18 : 20} 
        strokeWidth={isSub ? 2 : 1.5}
        className={`flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#4C1D95]'}`} 
      />
      
      {!isCollapsed && (
        <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'} truncate`}>
          {label}
        </span>
      )}
    </Link>
  );
};

// Configuration Array for Menu Items
const MENU_ITEMS = [
  {
    id: 'home',
    label: 'Home',
    icon: Home,
    submenu: [
      { path: '/admin/menubar', label: 'Menubar', icon: Menu },
      { path: '/admin/header-section', label: 'Header Section', icon: User },
      { path: '/admin/about-me', label: 'About Me', icon: FileText },
    ]
  },
  {
    id: 'projects',
    label: 'Projects',
    icon: Briefcase,
    submenu: [
      { path: '/admin/projects', label: 'All Projects', icon: Briefcase },
      { path: '/admin/categories', label: 'Categories', icon: Tag },
      { path: '/admin/subcategories', label: 'Subcategories', icon: FolderOpen },
    ]
  },
  {
    id: 'blogs',
    label: 'Blogs',
    icon: BookOpen,
    submenu: [
      { path: '/admin/blogs', label: 'All Blogs', icon: BookOpen },
      { path: '/admin/blog-categories', label: 'Categories', icon: FolderOpen },
    ]
  },
  {
    id: 'contact',
    label: 'Contact',
    icon: Mail,
    submenu: [
      { path: '/admin/contact-settings', label: 'Settings', icon: SettingsIcon },
      { path: '/admin/contact-responses', label: 'Responses', icon: MessageSquare },
    ]
  },
  {
    id: 'spatial',
    label: 'Spatial Canvas',
    icon: Map,
    submenu: [
      { path: '/admin/map-upload', label: 'Map Upload', icon: Upload },
      { path: '/admin/map-categories', label: 'Categories', icon: Tag },
      { path: '/admin/featured-maps', label: 'Featured', icon: Star },
    ]
  },
  {
    id: 'certificate',
    label: 'Certificates',
    icon: Award,
    submenu: [
      { path: '/admin/certificate-upload', label: 'Upload', icon: Upload },
      { path: '/admin/certificate-categories', label: 'Categories', icon: Tag },
      { path: '/admin/featured-certificates', label: 'Featured', icon: Star },
    ]
  },
  {
    id: 'experience',
    label: 'Experience',
    icon: Trophy,
    path: '/admin/experiences'
  },
  {
    id: 'gallery',
    label: 'Galleries',
    icon: Image,
    submenu: [
      { path: '/admin/project-gallery', label: 'Image Gallery', icon: Image },
      { path: '/admin/presentations', label: 'Presentations', icon: Presentation },
    ]
  },
  {
    id: 'shop',
    label: 'Shop',
    icon: ShoppingBag,
    submenu: [
      { path: '/admin/products', label: 'Products', icon: ShoppingBag },
      { path: '/admin/shop-categories', label: 'Categories', icon: Tag },
      { path: '/admin/coupons', label: 'Coupons', icon: Ticket },
      { path: '/admin/orders', label: 'Orders', icon: ClipboardList },
    ]
  },
  {
    id: 'newsletter',
    label: 'Newsletter',
    icon: Newspaper,
    path: '/admin/newsletter'
  },
  {
    id: 'shortlinks',
    label: 'Shortlinks',
    icon: Link2,
    path: '/admin/shortlinks'
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon,
    submenu: [
      { path: '/admin/settings', label: 'General Settings', icon: SettingsIcon },
      { path: '/admin/settings/authors', label: 'Authors', icon: User },
      { path: '/admin/settings/social-media', label: 'Social Media', icon: Share2 },
    ]
  },
  {
    id: 'rich-text-editor',
    label: 'Rich Text Editor',
    icon: FileText,
    path: '/admin/rich-text-editor'
  }
];


